import { useState } from 'react';
import { Compass, Eye, Building2, User } from 'lucide-react';
import { DiveMask } from '@/components/icons/DiveMask';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { useAuth } from '@/hooks/useAuth';
import { motion } from 'framer-motion';
import { ControlPanelExternalTrigger } from '@/components/layout/control-panel/AppControlPanel';

const matchesAnyPath = (pathname: string, patterns: string[]) =>
  patterns.some((pattern) => Boolean(matchPath(pattern, pathname)));

const BottomNav = () => {
  const { pathname } = useLocation();
  const { t } = useI18n();
  const { user } = useAuth();
  const [panelOpen, setPanelOpen] = useState(false);

  if (pathname.startsWith('/admin')) return null;

  // Public-first BottomNav: Guide | Sichtweiten | Karte
  // + (authenticated) "Mein Scuvio" → opens AppControlPanel drawer
  // + (anonymous)     "Login"       → /auth
  const isGuide = matchesAnyPath(pathname, [
    '/', '/index', '/discover', '/guide', '/results', '/site/:id', '/courses',
  ]);
  const isVisibility = matchesAnyPath(pathname, [
    '/visibility', '/report', '/site/:id/visibility',
  ]);
  const isPois = matchesAnyPath(pathname, ['/providers', '/providers/:id', '/poi/:id']);
  const isMap = matchesAnyPath(pathname, ['/map']);
  const isMein = matchesAnyPath(pathname, [
    '/my-diving', '/my-diving/*',
    '/community', '/community/*',
    '/profile', '/notifications',
    '/spot-studio', '/spot-studio/*',
  ]);

  const activeTab = isGuide
    ? '/guide'
    : isVisibility
      ? '/visibility'
      : isPois || isMap
        ? '/providers'
        : isMein
          ? user ? '__panel__' : '/auth'
          : undefined;

  type Tab = {
    key: string;
    labelKey: string;
    icon: typeof Compass;
    to?: string;
    onClick?: () => void;
    activeId: string;
    state?: unknown;
  };
  const tabs: Tab[] = [
    { key: 'guide', labelKey: 'nav.guide', icon: Compass, to: '/guide', activeId: '/guide' },
    { key: 'visibility', labelKey: 'nav.visibility', icon: Eye, to: '/visibility', activeId: '/visibility' },
    { key: 'providers', labelKey: 'nav.providers', icon: Building2, to: '/providers', activeId: '/providers' },
    user
      ? {
          key: 'meinScuvio',
          labelKey: 'nav.meinScuvio',
          icon: DiveMask,
          onClick: () => setPanelOpen(true),
          activeId: '__panel__',
        }
      : {
          key: 'signin',
          labelKey: 'nav.signin',
          icon: User,
          to: '/auth',
          state: { from: pathname },
          activeId: '/auth',
        },
  ];

  return (
    <>
    <ControlPanelExternalTrigger open={panelOpen} onOpenChange={setPanelOpen} />
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 pb-safe pointer-events-none"
      aria-label="Primary"
    >
      <div className="mx-3 mb-2 pointer-events-auto rounded-2xl bg-background/80 dark:bg-card/80 backdrop-blur-2xl border border-border/40 shadow-[0_8px_32px_-12px_hsl(var(--abyss)/0.25)] sm:max-w-xl sm:mx-auto">
        <ul className="flex items-stretch h-16 px-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.activeId;
            const sharedClasses =
              "relative h-full min-h-[44px] min-w-[44px] flex flex-col items-center justify-center gap-1 px-1 select-none touch-manipulation transition-transform duration-200 active:scale-[0.94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-0 rounded-xl";
            const inner = (
              <>
                {active && (
                  <motion.div
                    layoutId="bnav-active-pill"
                    className="absolute inset-x-2 inset-y-1.5 rounded-xl bg-primary/12"
                    transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  />
                )}
                <span className="relative flex flex-col items-center justify-center gap-0.5">
                  <Icon
                    className={`w-[22px] h-[22px] transition-colors duration-200 ${
                      active ? 'text-primary' : 'text-muted-foreground'
                    }`}
                    strokeWidth={active ? 2.2 : 1.75}
                    aria-hidden
                  />
                  <span
                    className={`text-[10.5px] leading-none font-display tracking-tight truncate max-w-full transition-colors duration-200 ${
                      active
                        ? 'text-primary font-semibold'
                        : 'text-muted-foreground font-medium'
                    }`}
                  >
                    {t(tab.labelKey)}
                  </span>
                </span>
              </>
            );
            return (
              <li key={tab.key} className="flex-1 min-w-0">
                {tab.to ? (
                  <Link
                    to={tab.to}
                    state={tab.state as never}
                    aria-current={active ? 'page' : undefined}
                    aria-label={t(tab.labelKey)}
                    className={sharedClasses}
                  >
                    {inner}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={tab.onClick}
                    aria-label={t(tab.labelKey)}
                    aria-haspopup="dialog"
                    aria-expanded={panelOpen}
                    className={`${sharedClasses} w-full bg-transparent`}
                  >
                    {inner}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
    </>
  );
};

export default BottomNav;
